import * as React from "react";
import { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Button,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Link } from "react-router-dom";
import img from '../assets/blog3.png';

export default function BlogPostPage3() {
  const theme = useTheme();

  // ✅ SCROLL TO TOP
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const post = {
    title: "Morbidity And Mortality Due To Fluid & Electrolyte Imbalance",
    author: "sudhanshu",
    date: "March 15, 2025",
    heroImg:
     img,
    intro:
      "Fluid and electrolyte imbalances are significant contributors to morbidity and mortality, particularly in vulnerable populations such as hospitalized patients, the elderly, and those with chronic conditions like kidney disease, heart failure, or liver disease. While exact global figures for deaths specifically due to fluid and electrolyte imbalances are challenging to pinpoint, here are some key insights:",
    sections: [
      {
        title: "Hospitalized Patients",
        bullets: [
          "Studies suggest that 30–40% of hospitalized patients experience some form of fluid or electrolyte imbalance, and these imbalances are associated with increased mortality rates.",
          "Hyponatremia (low sodium levels) alone is linked to a 2–5x higher risk of mortality in hospitalized patients.",
        ],
      },
      {
        title: "Chronic Kidney Disease (CKD) and Dialysis Patients",
        bullets: [
          "Patients with CKD, especially those on dialysis, are at high risk of fluid and electrolyte imbalances.",
          "Intradialytic hypotension (IDH), a complication of fluid mismanagement during dialysis, occurs in 10–30% of dialysis sessions and is associated with increased mortality.",
          "Approximately 20–25% of dialysis patients die annually, with cardiovascular events (often linked to fluid and electrolyte imbalances) being a leading cause.",
        ],
      },
      {
        title: "Global Burden",
        bullets: [
          "There is no single global statistic for deaths due to fluid and electrolyte imbalances, but they are a contributing factor in millions of deaths annually—particularly in patients with chronic illnesses, sepsis, or those in critical care settings.",
          "Acute kidney injury (AKI), often caused by fluid and electrolyte imbalances, affects over 13 million people annually and contributes to 7 million deaths per year.",
        ],
      },
      {
        title: "Elderly Population",
        bullets: [
          "The elderly are particularly susceptible to dehydration and electrolyte disturbances, which contribute to hospitalizations and mortality.",
          "In the U.S. alone, dehydration is one of the top 10 reasons for hospitalization in adults over 65, with associated mortality rates as high as 50% in severe cases.",
        ],
      },
      {
        title: "References / Where to Look (Suggested Sources)",
        bullets: [
          "Hyponatremia and Mortality in Hospitalized Patients: NEJM, JASN, Kidney International; Waikar et al. (2009).",
          "Intradialytic Hypotension (IDH) and Dialysis Patient Mortality: AJKD, CJASN; Flythe et al. (2015), Sands et al. (2014).",
          "Acute Kidney Injury (AKI) and Global Burden: Global Burden of Disease (GBD), ISN; a 2020 Lancet study is a good starting point.",
          "Dehydration in the Elderly: Journal of Gerontology, JAMDA; El-Sharkawy et al. (2017) in Age and Ageing.",
          "General Fluid and Electrolyte Imbalance Statistics: Harrison’s Principles of Internal Medicine; Brenner and Rector’s The Kidney.",
        ],
      },
    ],
    conclusion:
      "In summary, while fluid and electrolyte imbalances are not always the primary cause of death, they are a significant contributing factor in hundreds of thousands to millions of deaths annually, especially in high-risk populations. A solution aimed at improving fluid therapy management can address a critical unmet need and help reduce mortality in these patients.",
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f9fafb", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.2, sm: 3 },
            borderRadius: 4,
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 14px 40px rgba(0,0,0,0.06)",
            bgcolor: "#fff",
          }}
        >
          {/* HEADER */}
          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={900}>
              {post.title}
            </Typography>

            <Stack direction="row" spacing={2} color="text.secondary">
              <Stack direction="row" spacing={0.6} alignItems="center">
                <PersonOutlineIcon fontSize="small" />
                <Typography variant="body2">{post.author}</Typography>
              </Stack>

              <Stack direction="row" spacing={0.6} alignItems="center">
                <CalendarMonthIcon fontSize="small" />
                <Typography variant="body2">{post.date}</Typography>
              </Stack>
            </Stack>
          </Stack>

          {/* IMAGE */}
          <Grid container spacing={2.5} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3} />
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 240, sm: 300, md: 360 },
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.1)",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
                }}
              >
                <Box
                  component="img"
                  src={post.heroImg}
                  alt={post.title}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3} />
          </Grid>

          {/* OVERVIEW */}
          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 10px 26px rgba(0,0,0,0.05)",
              }}
            >
              <Typography variant="h6" fontWeight={900} mb={1}>
                Overview
              </Typography>

              <Typography
                variant="body1"
                sx={{ lineHeight: 1.9, color: "text.secondary" }}
              >
                {post.intro}
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
                <Chip label="Morbidity" size="small" />
                <Chip label="Mortality Risk" size="small" />
                <Chip label="AKI" size="small" />
                <Chip label="Dialysis" size="small" />
                <Chip label="Hyponatremia" size="small" />
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* CONTENT */}
          <Stack spacing={2.2}>
            {post.sections.map((sec, idx) => (
              <Box key={sec.title}>
                <Typography variant="h6" fontWeight={900} mb={1}>
                  {idx + 1}. {sec.title}
                </Typography>

                <List disablePadding>
                  {sec.bullets.map((b) => (
                    <ListItemButton
                      key={b}
                      disableRipple
                      sx={{
                        px: 0,
                        py: 0.6,
                        cursor: "default",
                        "&:hover": { bgcolor: "transparent" },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ lineHeight: 1.85 }}>
                            • {b}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* CONCLUSION */}
          <Box
            sx={{
              p: 2.2,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            }}
          >
            <Typography variant="h6" fontWeight={900} mb={1}>
              Conclusion
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
              {post.conclusion}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* PREVIOUS / NEXT */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Link to="/BlogPostPage2" style={{ textDecoration: "none" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackRoundedIcon />}
                  sx={{
                    justifyContent: "flex-start",
                    borderRadius: 3,
                    py: 1.4,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Previous Post
                </Button>
              </Link>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  justifyContent: "flex-end",
                  borderRadius: 3,
                  py: 1.4,
                  fontWeight: 800,
                  textTransform: "none",
                }}
                component={Link}
                to="/BlogPostPage4"
              >
                Next Post
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}
